import React, { useState, useEffect } from 'react';
import TrialCountdown from '../components/TrialCountdown';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Clock, 
  Heart, 
  Brain,
  Sparkles,
  Star,
  Zap,
  Target,
  Shield,
  Activity,
  BarChart3,
  TrendingUp,
  Lightbulb,
  Eye,
  EyeOff,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Plus,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronRight,
  Download,
  Share,
  Bookmark,
  Calendar,
  Users,
  MessageCircle,
  Headphones,
  Mic,
  MicOff,
  Wifi,
  WifiOff,
  Globe,
  Smartphone,
  Loader2,
  RotateCcw,
  Feather
} from 'lucide-react';
import api from '../utils/api';

const Meditation = () => {
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionContent, setSessionContent] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  const meditations = [
    {
      id: 1,
      title: 'Morning Mindfulness',
      subtitle: 'Start Your Day with Intention',
      duration: '10 min',
      category: 'Mindfulness',
      icon: Eye,
      color: 'from-amber-400 to-orange-500',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
      borderColor: 'border-amber-200',
      description: 'Begin your day with clarity and purpose. This gentle morning meditation helps you set positive intentions, cultivate gratitude, and establish a mindful foundation for the hours ahead.',
      benefits: ['Increased focus', 'Reduced morning stress', 'Better mood'],
      prompt: 'Create a 10-minute morning mindfulness meditation session focused on breath awareness and setting positive intentions for the day.'
    },
    {
      id: 2,
      title: 'Stress Relief',
      subtitle: 'Release Tension & Find Calm',
      duration: '15 min',
      category: 'Relaxation',
      icon: Shield,
      color: 'from-blue-400 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200',
      description: 'Let go of daily stress and tension with this deeply relaxing session. Using progressive muscle relaxation and calming ocean visualizations to help you find inner peace.',
      benefits: ['Reduced muscle tension', 'Lower stress hormones', 'Improved relaxation'],
      prompt: 'Provide a 15-minute stress relief meditation session with progressive muscle relaxation and calming visualizations.'
    },
    {
      id: 3,
      title: 'Sleep Preparation',
      subtitle: 'Drift into Peaceful Rest',
      duration: '20 min',
      category: 'Sleep',
      icon: EyeOff,
      color: 'from-indigo-400 to-purple-500',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
      borderColor: 'border-indigo-200',
      description: 'Prepare your mind and body for deep, restorative sleep. This gentle session uses body scan techniques and soothing breathing exercises to help you unwind.',
      benefits: ['Faster sleep onset', 'Deeper sleep quality', 'Reduced insomnia'],
      prompt: 'Guide a 20-minute sleep preparation meditation session with gentle breathing exercises and body scan techniques.'
    },
    {
      id: 4,
      title: 'Anxiety Calm',
      subtitle: 'Find Your Center',
      duration: '12 min',
      category: 'Anxiety',
      icon: Activity,
      color: 'from-green-400 to-emerald-500',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
      borderColor: 'border-green-200',
      description: 'Combat anxiety with grounding techniques and soothing affirmations. This session helps you reconnect with the present moment and find your inner calm.',
      benefits: ['Reduced anxiety', 'Better emotional balance', 'Improved focus'],
      prompt: 'Create a 12-minute anxiety relief meditation session with grounding techniques and soothing affirmations.'
    },
    {
      id: 5,
      title: 'Mindful Breathing',
      subtitle: 'Connect with Your Breath',
      duration: '8 min',
      category: 'Breathing',
      icon: Lightbulb,
      color: 'from-sky-400 to-blue-500',
      bgColor: 'bg-gradient-to-br from-sky-50 to-blue-50',
      borderColor: 'border-sky-200',
      description: 'Master the art of conscious breathing. This focused session teaches you breathing techniques that you can use anywhere to find instant calm and clarity.',
      benefits: ['Better breath control', 'Instant calm', 'Improved concentration'],
      prompt: 'Create an 8-minute mindful breathing meditation focusing on different breathing patterns and techniques.'
    },
    {
      id: 6,
      title: 'Body Awareness',
      subtitle: 'Honor Your Physical Self',
      duration: '18 min',
      category: 'Body Scan',
      icon: Target,
      color: 'from-teal-400 to-green-500',
      bgColor: 'bg-gradient-to-br from-teal-50 to-green-50',
      borderColor: 'border-teal-200',
      description: 'Develop a deeper connection with your body through gentle awareness. This body scan meditation helps you release physical tension and cultivate body-mind harmony.',
      benefits: ['Body awareness', 'Tension release', 'Mind-body connection'],
      prompt: 'Create an 18-minute body scan meditation session focusing on awareness of each part of the body.'
    }
  ];

  const handleStartSession = async (meditation) => {
    setActiveSession(meditation);
    setLoading(true);
    setSessionContent(null);

    try {
      // Call AI to generate the meditation session
      const response = await api.post('/meditation/generate', {
        prompt: meditation.prompt,
        duration: meditation.duration,
        category: meditation.category
      });

      setSessionContent(response.data.session);
    } catch (error) {
      console.error('Failed to generate meditation session:', error);
      // Fallback content if AI fails
      setSessionContent({
        title: meditation.title,
        duration: meditation.duration,
        content: `Welcome to your ${meditation.title} session. This ${meditation.duration} session will help you find peace and calm. Please find a comfortable position and close your eyes. Take a deep breath in... and exhale slowly. Let's begin your journey to inner peace.`,
        instructions: [
          'Find a comfortable seated position',
          'Close your eyes gently',
          'Take three deep breaths',
          'Follow the guided meditation',
          'Stay present and focused'
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Timer functions
  const startTimer = (durationMinutes) => {
    const durationSeconds = durationMinutes * 60;
    setTimeRemaining(durationSeconds);
    setTimerActive(true);
  };

  const pauseTimer = () => {
    setTimerActive(false);
  };

  const resumeTimer = () => {
    setTimerActive(true);
  };

  const resetTimer = () => {
    if (activeSession) {
      const durationMinutes = parseInt(activeSession.duration);
      startTimer(durationMinutes);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive, timeRemaining]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleEndSession = () => {
    setActiveSession(null);
    setSessionContent(null);
    setSessionStarted(false);
    setTimeRemaining(0);
    setTimerActive(false);
  };

  if (activeSession) {
    return (
      <div className="space-y-8">
        {/* Session Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleEndSession}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 bg-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
          >
            <span>← Back to Sessions</span>
          </button>
          <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg shadow-sm">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <Clock className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">{activeSession.duration}</span>
            </div>
            <div className="w-px h-4 bg-gray-300"></div>
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <Brain className="w-4 h-4 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">AI-Guided</span>
            </div>
          </div>
        </div>

        {/* Session Content */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">{activeSession.title}</h1>
            <p className="text-lg text-gray-600 mb-2">AI-Generated Meditation Session</p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Sparkles className="w-4 h-4 text-purple-500" />
                <span>Personalized</span>
              </div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Heart className="w-4 h-4 text-red-500" />
                <span>Mindful</span>
              </div>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-500" />
                <span>Guided</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl mb-6">
                <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Generating Your Session...</h3>
              <p className="text-lg text-gray-600 mb-4">Creating a personalized meditation experience for you</p>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          ) : sessionContent ? (
            <div className="space-y-6">
              {!sessionStarted ? (
                <>
                  {/* Session Instructions */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 p-8 rounded-2xl mb-8">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Session Instructions</h3>
                    </div>
                    <ul className="space-y-4">
                      {sessionContent.instructions?.map((instruction, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                            <span className="text-blue-600 text-sm font-bold">{index + 1}</span>
                          </div>
                          <span className="text-gray-700 text-lg leading-relaxed">{instruction}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Session Controls */}
                  <div className="flex justify-center space-x-4">
                    <button 
                      onClick={() => {
                        setSessionStarted(true);
                        const durationMinutes = parseInt(activeSession.duration);
                        startTimer(durationMinutes);
                      }}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-3"
                    >
                      <Play className="w-5 h-5" />
                      <span>Begin Your Journey</span>
                      <Sparkles className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={handleEndSession}
                      className="bg-white border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 flex items-center space-x-2"
                    >
                      <span>Not Now</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Active Session Content */}
                  <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-200 p-8 rounded-3xl shadow-xl">
                    <div className="text-center mb-8">
                      {/* Timer Display */}
                      <div className="mb-8">
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center shadow-2xl border-4 border-emerald-200">
                          <div className="text-center">
                            <div className="text-4xl font-bold text-gray-900 mb-1">
                              {formatTime(timeRemaining)}
                            </div>
                            <div className="text-sm text-gray-500 font-medium">remaining</div>
                          </div>
                        </div>
                        
                        {/* Timer Controls */}
                        <div className="flex justify-center space-x-4 mb-6">
                          {timerActive ? (
                            <button
                              onClick={pauseTimer}
                              className="p-3 bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                              title="Pause timer"
                            >
                              <Pause className="w-6 h-6" />
                            </button>
                          ) : (
                            <button
                              onClick={resumeTimer}
                              className="p-3 bg-emerald-100 text-emerald-700 rounded-full hover:bg-emerald-200 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                              title="Resume timer"
                            >
                              <Play className="w-6 h-6" />
                            </button>
                          )}
                          <button
                            onClick={resetTimer}
                            className="p-3 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110"
                            title="Reset timer"
                          >
                            <RotateCcw className="w-6 h-6" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Session Status */}
                      <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">Session in Progress</h3>
                      <p className="text-lg text-gray-600 mb-2">Follow the guided meditation below</p>
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                          <span>Live</span>
                        </div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-4 h-4 text-red-500" />
                          <span>Mindful</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Meditation Content */}
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-emerald-100">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Feather className="w-5 h-5 text-emerald-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900">Guided Meditation</h4>
                      </div>
                      <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line text-lg">
                          {sessionContent.content}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Session Controls */}
                  <div className="flex justify-center space-x-4">
                    <button 
                      onClick={handleEndSession}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center space-x-3"
                    >
                      <CheckCircle className="w-5 h-5" />
                      <span>Complete Session</span>
                      <Star className="w-5 h-5" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Trial Countdown */}
      <TrialCountdown />
      
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-2xl mb-6 shadow-lg">
          <Brain className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Meditation & Mindfulness</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Discover inner peace and mental clarity with our AI-powered guided meditation sessions. 
          Each session is thoughtfully crafted to help you find calm, reduce stress, and cultivate mindfulness.
        </p>
        <div className="flex items-center justify-center space-x-6 mt-6 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span>AI-Generated</span>
          </div>
          <div className="flex items-center space-x-2">
            <Heart className="w-4 h-4 text-red-500" />
            <span>Personalized</span>
          </div>
          <div className="flex items-center space-x-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span>Proven Techniques</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {meditations.map((meditation) => {
          const IconComponent = meditation.icon;
          return (
            <div 
              key={meditation.id} 
              className={`${meditation.bgColor} ${meditation.borderColor} border-2 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group`}
            >
              {/* Header with Icon and Category */}
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${meditation.color} shadow-lg`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-white/80 backdrop-blur-sm border ${meditation.borderColor} text-gray-700`}>
                  {meditation.category}
                </span>
              </div>

              {/* Title and Subtitle */}
              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-gray-800 transition-colors">
                  {meditation.title}
                </h3>
                <p className="text-sm text-gray-600 font-medium">
                  {meditation.subtitle}
                </p>
              </div>

              {/* Duration */}
              <div className="flex items-center space-x-2 mb-4">
                <div className={`p-2 rounded-lg bg-gradient-to-br ${meditation.color} bg-opacity-10`}>
                  <Clock className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {meditation.duration}
                </span>
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {meditation.description}
              </p>

              {/* Benefits */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Benefits
                </h4>
                <div className="flex flex-wrap gap-2">
                  {meditation.benefits.map((benefit, index) => (
                    <span 
                      key={index}
                      className={`px-2 py-1 rounded-full text-xs font-medium bg-white/60 backdrop-blur-sm border ${meditation.borderColor} text-gray-700`}
                    >
                      {benefit}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Button */}
              <button 
                onClick={() => handleStartSession(meditation)}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r ${meditation.color} hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2 group`}
              >
                <Play className="w-4 h-4" />
                <span>Begin Session</span>
                <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Meditation; 